/**
 * useAuth Hook
 *
 * Convenience hook for accessing auth state and actions.
 * Wraps the Zustand auth store for simpler usage.
 */

import { useAuthStore } from '@/store';
import type { CurrentUser, LoginRequest } from '@/api/types';

/**
 * useAuth - Access authentication state and actions
 *
 * @example
 * const { user, isAuthenticated, login, logout } = useAuth();
 *
 * if (!isAuthenticated) {
 *   return <LoginPage />;
 * }
 */
export function useAuth() {
  const {
    user,
    isAuthenticated,
    isLoading,
    error,
    isInitialized,
    login,
    logout,
    fetchCurrentUser,
    clearError,
    initialize,
  } = useAuthStore();

  return {
    // State
    /** Current authenticated user */
    user,
    /** Whether user is authenticated */
    isAuthenticated,
    /** Loading state for auth operations */
    isLoading,
    /** Error message from last operation */
    error,
    /** Whether initial auth check has completed */
    isInitialized,

    // Actions
    /** Login with credentials */
    login,
    /** Logout and clear session */
    logout,
    /** Fetch current user details */
    fetchCurrentUser,
    /** Clear error message */
    clearError,
    /** Initialize auth state (check existing token) */
    initialize,
  };
}

// ============================================
// Type exports for convenience
// ============================================

export type { CurrentUser, LoginRequest };
